/**
 * Sentry frontend init (Iter 142.6)
 * =================================
 *
 * Browser-side error tracking + tracing + session replay (errors only).
 * Initialized at the top of `index.js` before React renders so we capture
 * boot-time crashes too.
 *
 * Design choices:
 *  - DSN comes from REACT_APP_SENTRY_DSN — when not set, Sentry is
 *    fully disabled. Lets local devs skip the noise without code changes.
 *  - Replay is throttled: 0% on every session, 100% only when an error
 *    fires (`replaysOnErrorSampleRate=1.0`). Keeps the data volume sane
 *    while still giving you the playback when you actually need it.
 *  - Replay privacy is locked DOWN by default (`maskAllText`,
 *    `maskAllInputs`, `blockAllMedia`) — we are a payments + gaming
 *    platform; players' usernames/passwords/payment URLs MUST NOT leak
 *    into Sentry.
 *  - `beforeSend` scrubs auth tokens + bearer headers + payment URLs from
 *    the error payload before transmission. Defense in depth.
 */
import * as Sentry from '@sentry/react';

const _SENSITIVE_PATTERNS = [
  /Bearer\s+[A-Za-z0-9._-]{20,}/gi,
  /eyJ[A-Za-z0-9._-]{40,}/g, // JWT
  /https?:\/\/[^\s]*paylink[^\s]*/g, // payment URLs
  /https?:\/\/[^\s]*invoice[^\s]*/g,
];

function scrubString(s) {
  if (typeof s !== 'string') return s;
  let out = s;
  _SENSITIVE_PATTERNS.forEach((p) => {
    out = out.replace(p, '[REDACTED]');
  });
  return out;
}

function scrubBreadcrumb(bc) {
  if (!bc) return bc;
  if (typeof bc.message === 'string') bc.message = scrubString(bc.message);
  if (bc.data) {
    Object.keys(bc.data).forEach((k) => {
      const lk = k.toLowerCase();
      if (
        lk.includes('password') ||
        lk.includes('token') ||
        lk.includes('authorization') ||
        lk.includes('secret') ||
        lk.includes('cookie') ||
        lk.includes('pay_url') ||
        lk.includes('payurl')
      ) {
        bc.data[k] = '[REDACTED]';
      } else if (typeof bc.data[k] === 'string') {
        bc.data[k] = scrubString(bc.data[k]);
      }
    });
  }
  return bc;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Local dev — silently skip.
    return false;
  }

  const traces = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1');
  const replays = parseFloat(process.env.REACT_APP_SENTRY_REPLAYS_SAMPLE_RATE || '0.0');
  const replaysOnError = parseFloat(
    process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'
  );
  const environment =
    process.env.REACT_APP_SENTRY_ENVIRONMENT ||
    (process.env.NODE_ENV === 'development' ? 'development' : 'production');
  const release =
    process.env.REACT_APP_SENTRY_RELEASE ||
    process.env.REACT_APP_RELEASE ||
    'vegas-lobby-web@unknown';

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: traces,
    replaysSessionSampleRate: replays,
    replaysOnErrorSampleRate: replaysOnError,
    sendDefaultPii: false,
    // Filter common dev/preview noise so we don't drown in low-signal events.
    ignoreErrors: [
      // Network blips / cancelled fetches — usually transient
      'Network Error',
      'AbortError',
      'cancelled',
      // ResizeObserver chatter (browser quirk, never actionable)
      'ResizeObserver loop completed',
      'ResizeObserver loop limit exceeded',
      // Browser extensions injecting code
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
    ],
    beforeSend(event) {
      try {
        // Scrub request data
        if (event.request) {
          if (event.request.headers) {
            Object.keys(event.request.headers).forEach((k) => {
              if (k.toLowerCase().match(/^(authorization|cookie|x-api-key|x-signature|x-webhook-secret)$/)) {
                event.request.headers[k] = '[REDACTED]';
              }
            });
          }
          if (typeof event.request.data === 'string') {
            event.request.data = scrubString(event.request.data);
          }
        }
        // Scrub breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
        }
        // Scrub exception messages
        if (event.exception?.values) {
          event.exception.values.forEach((ev) => {
            if (typeof ev.value === 'string') ev.value = scrubString(ev.value);
          });
        }
      } catch (err) {
        // Never let scrubbing break event delivery
        // eslint-disable-next-line no-console
        console.warn('[Sentry] scrub failed:', err);
      }
      return event;
    },
    beforeBreadcrumb(bc) {
      // Drop loud / low-signal breadcrumbs
      if (bc.category === 'console' && bc.level === 'log') return null;
      return scrubBreadcrumb(bc);
    },
  });
  return true;
}

/**
 * Pin the current player to all subsequent events on this scope.
 * Call right after a successful login, and again with `null` on logout.
 */
export function setSentryUser(user) {
  try {
    if (!user || !user.id) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({
      id: String(user.id),
      username: user.username || undefined,
      role: user.role || undefined,
    });
  } catch (_) {
    /* never let observability break the login path */
  }
}

/**
 * Wrap a React component so any throw inside its subtree is captured by
 * Sentry and shows a friendly fallback. Use sparingly — putting one
 * boundary at the app root catches everything.
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
